import React, { useState } from 'react'
import type { UsagePurchaseSource } from '@shared/common-types.js'

const numberFormat = new Intl.NumberFormat('zh-CN')

/** 默认显示几个入口，多的点「显示全部」 */
const COLLAPSED_ROWS = 8

/** 去向的四段。成功 / 失败用状态色，取消是用户自己退出的，用中性灰 */
const OUTCOMES = [
  { key: 'completed', label: '成功', className: 'bg-green-500', textClassName: 'text-green-600' },
  { key: 'cancelled', label: '取消', className: 'bg-gray-400', textClassName: 'text-gray-500 dark:text-gray-300' },
  { key: 'failed', label: '失败', className: 'bg-red-500', textClassName: 'text-red-500' },
] as const

/** 开始购买了、但这段时间里还没有成功 / 取消 / 失败（App 被杀、还在付款页上） */
const PENDING = { label: '无结果', className: 'bg-gray-200 dark:bg-slate-500' }

function OutcomeBar({ row, maxStarted }: { row: UsagePurchaseSource; maxStarted: number }) {
  const outcomes = row.completed + row.cancelled + row.failed
  const pending = Math.max(0, row.started - outcomes)
  // 结果可能来自时段开始前的购买，比 started 多；宽度按两者里大的算
  const total = Math.max(row.started, outcomes)
  if (total === 0) return <span className="text-xs text-gray-400">没人开始购买</span>
  return (
    <div className="flex min-w-0 items-center gap-2">
      {/* 条的长度跟开始购买次数走，入口之间能比量；里面按去向分段，能比比例 */}
      <div
        className="flex h-3 min-w-[4px] gap-px overflow-hidden rounded"
        style={{ width: `${(total / maxStarted) * 100}%` }}
      >
        {OUTCOMES.map((outcome) =>
          row[outcome.key] > 0 ? (
            <span key={outcome.key} className={outcome.className} style={{ flexGrow: row[outcome.key] }} />
          ) : null,
        )}
        {pending > 0 && <span className={PENDING.className} style={{ flexGrow: pending }} />}
      </div>
      <span className="flex shrink-0 gap-1.5 text-xs tabular-nums">
        {OUTCOMES.map((outcome) =>
          row[outcome.key] > 0 ? (
            <span key={outcome.key} className={outcome.textClassName}>
              {outcome.label} {row[outcome.key]}
            </span>
          ) : null,
        )}
      </span>
    </div>
  )
}

function conversion(row: UsagePurchaseSource): string {
  if (row.viewers === 0) return '—'
  const rate = row.starters / row.viewers
  return rate > 0 && rate < 0.005 ? '<1%' : `${Math.round(rate * 100)}%`
}

/**
 * 付费入口：每个 source 一行，看到付费墙的人数、开始购买、转化率（按人数），最后一条成功 / 取消 / 失败的堆叠条。
 * 用来回答「哪个入口点了不买」（取消多）和「哪个入口买不成」（失败多）。
 */
export default function PurchaseSourcesCard({ sources }: { sources: UsagePurchaseSource[] }) {
  const [expanded, setExpanded] = useState(false)
  const rows = sources.filter((row) => row.viewers > 0 || row.started > 0)
  const maxStarted = Math.max(
    1,
    ...rows.map((row) => Math.max(row.started, row.completed + row.cancelled + row.failed)),
  )
  const visible = expanded ? rows : rows.slice(0, COLLAPSED_ROWS)

  return (
    <div className="flex flex-col rounded-xl bg-white p-3 dark:bg-slate-700 md:p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <span className="text-base font-semibold dark:text-white">付费入口</span>
        <span className="flex gap-3 text-xs text-gray-500 dark:text-gray-400">
          {[...OUTCOMES, PENDING].map((item) => (
            <span key={item.label} className="flex items-center gap-1">
              <span className={`inline-block h-2.5 w-2.5 rounded-sm ${item.className}`} />
              {item.label}
            </span>
          ))}
        </span>
      </div>
      {rows.length === 0 ? (
        <div className="flex h-16 items-center justify-center text-sm text-gray-400">这段时间没人看到付费墙</div>
      ) : (
        <>
          <div className="hidden gap-3 border-b border-gray-100 pb-1 text-xs text-gray-500 dark:border-slate-600 dark:text-gray-400 md:grid md:grid-cols-[minmax(0,1.2fr)_64px_72px_48px_minmax(0,2fr)]">
            <span>入口</span>
            <span className="text-right">看到（人）</span>
            <span className="text-right">开始购买</span>
            <span className="text-right">转化</span>
            <span>去向</span>
          </div>
          <ul className="flex flex-col">
            {visible.map((row) => (
              <li
                key={row.source}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 border-b border-gray-50 py-1.5 text-sm last:border-0 dark:border-slate-600/50 md:grid-cols-[minmax(0,1.2fr)_64px_72px_48px_minmax(0,2fr)]"
              >
                <span className="truncate" title={row.source}>
                  {row.source || '（没带 source）'}
                </span>
                {/* 手机上三个数挤成一行放在入口右边；桌面上各占一列 */}
                <span className="whitespace-nowrap text-right text-xs tabular-nums text-gray-500 dark:text-gray-400 md:hidden">
                  看到 {numberFormat.format(row.viewers)} · 开始 {row.started} · {conversion(row)}
                </span>
                <span className="hidden text-right tabular-nums md:block">{numberFormat.format(row.viewers)}</span>
                <span className="hidden text-right tabular-nums md:block">
                  {row.started}
                  {row.starters !== row.started && (
                    <span className="ml-1 text-xs text-gray-400">{row.starters} 人</span>
                  )}
                </span>
                <span className="hidden text-right tabular-nums md:block">{conversion(row)}</span>
                <div className="col-span-2 md:col-span-1">
                  <OutcomeBar row={row} maxStarted={maxStarted} />
                </div>
              </li>
            ))}
          </ul>
          {rows.length > COLLAPSED_ROWS && (
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              className="mt-1 self-center rounded-md py-1 text-sm text-blue-500"
            >
              {expanded ? '收起' : `显示全部 ${rows.length} 个入口`}
            </button>
          )}
        </>
      )}
      <p className="mt-2 text-[11px] text-gray-400">
        转化 = 开始购买的人 ÷ 看到付费墙的人；条的长度是开始购买次数，里面按去向分段。只看当前时段，只受国家 /
        版本过滤。
      </p>
    </div>
  )
}
